import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import TemplatePicker, { withTemplate } from "@/components/TemplatePicker";
import { fmtL, fmtMon, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import DocumentCenter from "@/components/DocumentCenter";
import { PlusIcon, EyeIcon, XIcon, CheckCircleIcon, ProhibitIcon, PackageIcon, FileTextIcon, TrashIcon, ArrowLeftIcon, ClockCounterClockwiseIcon, WarehouseIcon, FilePdfIcon, PaperPlaneTiltIcon, EnvelopeIcon, SpinnerIcon, CheckIcon, MoneyIcon, ReceiptIcon, PencilSimpleIcon, PlantIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { ConfigContableCompras, GastosPosteriores } from "@/pages/_purchases/ComprasIntegracion";
import { useAuth } from "@/contexts/AuthContext";
import PagoCompraModal, { PagosFactura, HistorialEdiciones, monedaDe, saldoMoneda, ESTADO_PAGO } from "@/pages/_purchases/PagoCompra";
import { getToken } from "@/lib/token";

// Abre un PDF autenticado en pestaña nueva
export async function openAuthPdf(path) {
  try {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = getToken();
    const companyId = localStorage.getItem("agroport_company_id");
    const headers = { Authorization: `Bearer ${token}` };
    if (companyId) headers["X-Company-Id"] = companyId;
    const res = await fetch(`${apiBase}${path}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    toast.error(`Error abriendo PDF: ${e.message}`);
  }
}

const poPendingToInvoice = (o) => (o.items || []).some((it) => Number(it.cantidad_recibida || 0) - Number(it.cantidad_facturada || 0) > 0.0001);

export default function Purchases() {
  const [tab, setTab] = useState("invoices");
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [backorders, setBackorders] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [receiving, setReceiving] = useState(null);
  const [sendingPo, setSendingPo] = useState(null);
  const [configCompras, setConfigCompras] = useState(false);
  const [gastosDe, setGastosDe] = useState(null);       // factura a la que se agregan gastos posteriores
  const { user: usuario } = useAuth();
  const puedeConfig = usuario?.role === "admin" || usuario?.role === "accountant" || Boolean(usuario?.effective?.is_admin);
  const esAdmin = usuario?.role === "admin" || Boolean(usuario?.effective?.is_admin);   // edición de facturas (también validado en el servidor)
  const [pagando, setPagando] = useState(null);          // factura a pagar (modal en la misma pantalla)
  const [cuentas, setCuentas] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const [o, i, b, r] = await Promise.all([
      api.get("/purchase-orders"),
      api.get("/purchase-invoices"),
      api.get("/backorders/purchases").catch(() => ({ data: [] })),
      api.get("/goods-receipts").catch(() => ({ data: [] })),
    ]);
    setOrders(o.data); setInvoices(i.data);
    setBackorders(b.data || []);
    setReceipts(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const approve = async (po) => {
    try { await api.post(`/purchase-orders/${po.id}/approve`); toast.success("OC aprobada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const voidPO = async (po) => {
    if (!window.confirm(`¿Anular ${po.numero}?`)) return;
    try { await api.post(`/purchase-orders/${po.id}/void`); toast.success("OC anulada"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const voidInv = async (inv) => {
    const motivo = window.prompt(`Anular la factura ${inv.numero_interno} (${inv.proveedor_nombre}).
Se revierten inventario, cuenta por pagar / pago, asiento y gastos ligados.

Motivo de la anulación:`);
    if (!motivo || !motivo.trim()) return;
    try { await api.post(`/purchase-invoices/${inv.id}/void`, { motivo }); toast.success("Factura anulada · inventario, CxP y asiento revertidos"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const viewInv = async (inv) => { const { data } = await api.get(`/purchase-invoices/${inv.id}`); setViewing(data); };
  const invPdf = (inv) => openAuthPdf(`/purchase-invoices/${inv.id}/pdf`);
  const goPay = async (inv) => {
    try {
      const [{ data: f }, { data: ctas }] = await Promise.all([api.get(`/purchase-invoices/${inv.id}`), api.get("/treasury/accounts")]);
      setCuentas(ctas || []); setPagando(f);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const editInv = (inv) => navigate(`/compras/factura/${inv.id}/editar`);

  const poPdf = async (po) => {
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const token = getToken();
      const res = await fetch(withTemplate(`${apiBase}/purchase-orders/${po.id}/pdf`, "orden_compra"), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(`Error abriendo PDF: ${e.message}`);
    }
  };

  const totalOC = orders.reduce((s, o) => o.estado !== "anulada" ? s + o.total : s, 0);
  const totalFP = invoices.reduce((s, i) => i.estado !== "anulada" ? s + i.total : s, 0);

  return (
    <Layout
      title="Compras"
      subtitle={`${orders.length} órdenes · ${invoices.length} facturas de proveedor · Total ${fmtL(totalFP)}`}
      actions={
        <div className="flex gap-2 items-center flex-wrap">
          <TemplatePicker tipo="orden_compra" />
          {puedeConfig && <button onClick={() => setConfigCompras(true)} data-testid="compras-config-btn" className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary">Configuración contable</button>}
          <button onClick={() => navigate("/compras/oc/nueva")} data-testid="new-po-btn" className="px-3 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary hover:border-agro-yellow/40 flex items-center gap-2">
            <FileTextIcon size={14} /> Nueva OC
          </button>
          <button onClick={() => navigate("/compras/factura/nueva")} data-testid="new-purchase-invoice-btn" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={16} weight="bold" /> Nueva factura
          </button>
        </div>
      }
    >
      <div className="border-b border-border mb-4 flex gap-1 flex-wrap">
        {[
          { k: "invoices", l: `Facturas de proveedor (${invoices.length})` },
          { k: "orders", l: `Órdenes de compra (${orders.length})` },
          { k: "pendientes", l: `Pendientes (${backorders.length})` },
          { k: "recepciones", l: `Recepciones (${receipts.length})` },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative ${tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"}`}>
            {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "invoices" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">N° interno</th><th className="p-3">Proveedor</th><th className="p-3">Sucursal · tipo</th>
                <th className="p-3">N° proveedor</th><th className="p-3">CAI</th>
                <th className="p-3">Fecha</th><th className="p-3">Vence</th>
                <th className="p-3 text-right">Total</th><th className="p-3 text-right">Saldo</th>
                <th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acc.</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? <tr><td colSpan="11" className="p-12 text-center text-text-muted">Sin facturas</td></tr>
              : invoices.map((f) => (
                <tr key={f.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`pi-row-${f.numero_interno}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{f.numero_interno}</td>
                  <td className="p-3 text-sm">{f.proveedor_nombre}
                    {f.subcuenta_nombre && <div className="text-[10px] text-agro-green" data-testid={`pi-subcuenta-${f.numero_interno}`}>{f.subcuenta_codigo} · {f.subcuenta_nombre}</div>}
                    <div className="text-[10px] text-text-muted">{f.proveedor_rtn || "—"}</div></td>
                  <td className="p-3 text-xs">{f.branch_codigo || "—"}
                    <div className="text-[10px] text-text-muted">{f.tipo_documento === "gasto_compra" ? `Gasto de ${f.gasto_de_factura_numero}` : f.tipo_compra === "importacion" ? `Importación${f.expediente ? ` · ${f.expediente}` : ""}` : f.tipo_compra === "nacional" ? "Nacional" : ""}</div>
                    {f.auxiliar_codigo && <div className="text-[10px] font-mono text-text-muted">{f.auxiliar_codigo}</div>}</td>
                  <td className="p-3 text-xs font-mono">{f.numero_proveedor}</td>
                  <td className="p-3 text-xs font-mono text-text-muted">{f.cai_proveedor || "—"}</td>
                  <td className="p-3 text-xs">{fmtDate(f.fecha)}</td>
                  <td className="p-3 text-xs text-text-secondary">{f.fecha_vencimiento ? fmtDate(f.fecha_vencimiento) : "—"}</td>
                  <td className="p-3 text-right text-sm">{fmtMon(f.total_moneda ?? f.total, monedaDe(f))}
                    {monedaDe(f) === "USD" && <div className="text-[10px] text-text-muted">{fmtL(f.total)} · tasa {f.tipo_cambio}</div>}</td>
                  <td className="p-3 text-right text-sm text-agro-yellow">{fmtMon(saldoMoneda(f), monedaDe(f))}
                    {monedaDe(f) === "USD" && <div className="text-[10px] text-text-muted">{fmtL(f.saldo_pendiente)}</div>}</td>
                  <td className="p-3"><EstadoBadge e={f.estado} factura /></td>
                  <td className="p-3 pr-5 text-right">
                    <button aria-label="Ver" onClick={() => viewInv(f)} className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`view-pi-${f.numero_interno}`}><EyeIcon size={16} /></button>
                    <button onClick={() => invPdf(f)} title="PDF / imprimir" className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`pdf-pi-${f.numero_interno}`}><FilePdfIcon size={16} /></button>
                    {f.estado !== "anulada" && f.saldo_pendiente > 0 && <button onClick={() => goPay(f)} title="Registrar pago" className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`pay-pi-${f.numero_interno}`}><MoneyIcon size={16} /></button>}
                    {esAdmin && f.estado !== "anulada" && f.tipo_documento !== "gasto_compra" && <button onClick={() => editInv(f)} title="Editar (Administrador)" className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`edit-pi-${f.numero_interno}`}><PencilSimpleIcon size={16} /></button>}
                    {f.estado !== "anulada" && f.tipo_documento !== "gasto_compra" && (f.items || []).some((x) => x.entry_id) && <button onClick={() => setGastosDe(f)} title="Gastos posteriores (ajuste de costo)" className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`gastos-pi-${f.numero_interno}`}><ReceiptIcon size={16} /></button>}
                    {f.estado !== "anulada" && f.tipo_documento !== "gasto_compra" && <button onClick={() => voidInv(f)} title="Anular" className="p-1.5 text-text-muted hover:text-red-400" data-testid={`void-pi-${f.numero_interno}`}><ProhibitIcon size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Número</th><th className="p-3">Proveedor</th>
                <th className="p-3">Fecha</th><th className="p-3">Entrega</th>
                <th className="p-3">Forma pago</th><th className="p-3 text-right">Total</th>
                <th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acc.</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? <tr><td colSpan="8" className="p-12 text-center text-text-muted">Sin órdenes</td></tr>
              : orders.map((o) => (
                <tr key={o.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`po-row-${o.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{o.numero}</td>
                  <td className="p-3 text-sm">{o.proveedor_nombre}</td>
                  <td className="p-3 text-xs">{fmtDate(o.fecha)}</td>
                  <td className="p-3 text-xs text-text-secondary">{o.fecha_entrega_estimada ? fmtDate(o.fecha_entrega_estimada) : "—"}</td>
                  <td className="p-3 text-xs uppercase">{o.forma_pago}{o.forma_pago === "credito" && o.dias_credito ? ` ${o.dias_credito}d` : ""}</td>
                  <td className="p-3 text-right text-sm">{fmtL(o.total)}</td>
                  <td className="p-3"><EstadoBadge e={o.estado} /></td>
                  <td className="p-3 pr-5 text-right">
                    <button onClick={() => poPdf(o)} title="Ver / descargar PDF" data-testid={`pdf-po-${o.numero}`} className="p-1.5 text-text-muted hover:text-agro-green"><FilePdfIcon size={16} /></button>
                    <button onClick={() => setSendingPo(o)} title="Enviar al proveedor por email" data-testid={`email-po-${o.numero}`} className="p-1.5 text-text-muted hover:text-agro-green"><EnvelopeIcon size={16} /></button>
                    {o.estado === "borrador" && <button onClick={() => approve(o)} title="Aprobar" data-testid={`approve-po-${o.numero}`} className="p-1.5 text-text-muted hover:text-agro-green"><CheckCircleIcon size={16} weight="duotone" /></button>}
                    {(o.estado === "aprobada" || o.estado === "recibida_parcial") && <button onClick={() => setReceiving(o)} title="Recibir mercancía" data-testid={`receive-po-${o.numero}`} className="p-1.5 text-text-muted hover:text-agro-yellow"><PackageIcon size={16} weight="duotone" /></button>}
                    {["recibida_parcial", "recibida"].includes(o.estado) && poPendingToInvoice(o) && <button onClick={() => navigate(`/compras/factura/nueva?po_id=${o.id}`)} title="Facturar lo recibido" data-testid={`invoice-po-${o.numero}`} className="p-1.5 text-text-muted hover:text-agro-green"><ReceiptIcon size={16} weight="duotone" /></button>}
                    {!["recibida", "facturada", "anulada"].includes(o.estado) && <button onClick={() => voidPO(o)} title="Anular" aria-label="Anular" className="p-1.5 text-text-muted hover:text-red-400"><ProhibitIcon size={16} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configCompras && <ConfigContableCompras onClose={() => setConfigCompras(false)} />}
      {gastosDe && <GastosPosteriores factura={gastosDe} onClose={() => setGastosDe(null)} onSaved={load} />}
      {viewing && <PurchaseInvoiceViewer inv={viewing} onClose={() => setViewing(null)} onPdf={() => invPdf(viewing)}
        onPay={() => { const f = viewing; setViewing(null); goPay(f); }} onEdit={esAdmin ? () => editInv(viewing) : null} />}
      {pagando && <PagoCompraModal factura={pagando} accounts={cuentas} onClose={() => setPagando(null)} onSaved={load} />}
      {receiving && <ReceiveModal po={receiving} onClose={() => setReceiving(null)} onSaved={() => { setReceiving(null); load(); }} />}
      {sendingPo && <SendPOEmailModal po={sendingPo} onClose={() => setSendingPo(null)} />}

      {tab === "pendientes" && (
        <PurchaseBackordersTab items={backorders} onReceive={(b) => {
          const oc = orders.find((o) => o.id === b.oc_id);
          if (oc) setReceiving(oc);
          else toast.error("OC no encontrada en la lista");
        }} />
      )}

      {tab === "recepciones" && <ReceiptsTab items={receipts} />}
    </Layout>
  );
}

function PurchaseBackordersTab({ items, onReceive }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="purchase-backorders-tab">
      <div className="p-4 border-b border-border/60">
        <span className="text-[10px] uppercase tracking-wider text-text-muted">
          Órdenes de compra con mercadería pendiente de recibir
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">OC</th>
              <th className="p-3">Proveedor</th>
              <th className="p-3">Fecha</th>
              <th className="p-3">Entrega est.</th>
              <th className="p-3 text-right">Líneas pend.</th>
              <th className="p-3 text-right">Cant. pendiente</th>
              <th className="p-3">Estado</th>
              <th className="p-3 pr-5 text-right">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="8" className="p-12 text-center text-text-muted">Sin OCs pendientes de recibir</td></tr>
            ) : items.map((b) => (
              <PurchaseBackorderRow key={b.oc_id} b={b} onReceive={onReceive} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PurchaseBackorderRow({ b, onReceive }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`po-backorder-row-${b.numero}`}>
        <td className="p-3 pl-5 font-mono text-xs text-agro-green">{b.numero}</td>
        <td className="p-3 text-sm">{b.proveedor_nombre}</td>
        <td className="p-3 text-xs text-text-secondary">{fmtDate(b.fecha)}</td>
        <td className="p-3 text-xs">{b.fecha_entrega_estimada ? fmtDate(b.fecha_entrega_estimada) : "—"}</td>
        <td className="p-3 text-right text-sm">{b.items_pendientes}</td>
        <td className="p-3 text-right text-sm text-agro-yellow font-medium">{b.total_pendientes}</td>
        <td className="p-3 text-xs"><EstadoBadge e={b.estado} /></td>
        <td className="p-3 pr-5 text-right">
          <button onClick={() => setOpen(!open)} className="p-1.5 text-text-muted hover:text-agro-green" title={open ? "Ocultar" : "Ver líneas"} aria-label={open ? "Ocultar" : "Ver líneas"}><EyeIcon size={16} /></button>
          <button onClick={() => onReceive(b)} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Recibir mercancía" data-testid={`po-recv-${b.numero}`}><PackageIcon size={16} weight="duotone" /></button>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border/20 bg-app/30">
          <td colSpan="8" className="p-3 pl-10">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Líneas pendientes:</div>
            <table className="w-full text-xs">
              <thead><tr className="text-text-muted">
                <th className="text-left p-1">Código</th><th className="text-left p-1">Descripción</th>
                <th className="text-right p-1">Pedido</th><th className="text-right p-1">Recibido</th><th className="text-right p-1">Pendiente</th>
              </tr></thead>
              <tbody>
                {b.items.map((it) => (
                  <tr key={it.producto_id}>
                    <td className="p-1 font-mono text-agro-green">{it.codigo}</td>
                    <td className="p-1">{it.descripcion}</td>
                    <td className="p-1 text-right">{it.cantidad_pedida}</td>
                    <td className="p-1 text-right text-text-secondary">{it.cantidad_recibida}</td>
                    <td className="p-1 text-right text-agro-yellow font-medium">{it.cantidad_pendiente}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function ReceiptsTab({ items }) {
  const [open, setOpen] = useState({});
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="receipts-tab">
      <div className="p-4 border-b border-border/60 flex items-center gap-2">
        <WarehouseIcon size={14} className="text-agro-yellow" weight="duotone" />
        <span className="text-[10px] uppercase tracking-wider text-text-muted">
          Historial de recepciones de mercadería (por OC, alimenta PEPS)
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Referencia OC</th>
              <th className="p-3">Fecha</th>
              <th className="p-3 text-right">Líneas</th>
              <th className="p-3 text-right">Total uds.</th>
              <th className="p-3 text-right">Costo total</th>
              <th className="p-3">Bodegas</th>
              <th className="p-3 pr-5 text-right">Acc.</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-12 text-center text-text-muted">Sin recepciones registradas</td></tr>
            ) : items.map((r) => {
              const isOpen = !!open[r.referencia];
              return (
                <React.Fragment key={r.referencia}>
                  <tr className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`receipt-row-${r.referencia}`}>
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{r.referencia}</td>
                    <td className="p-3 text-xs">{fmtDate(r.fecha)}</td>
                    <td className="p-3 text-right text-sm">{r.total_lineas}</td>
                    <td className="p-3 text-right text-sm">{r.total_unidades}</td>
                    <td className="p-3 text-right text-sm font-medium">{fmtL(r.total_costo)}</td>
                    <td className="p-3 text-xs text-text-secondary">{(r.bodegas || []).join(", ") || "—"}</td>
                    <td className="p-3 pr-5 text-right">
                      <button onClick={() => setOpen({ ...open, [r.referencia]: !isOpen })} className="p-1.5 text-text-muted hover:text-agro-green" title="Ver detalle"><EyeIcon size={16} /></button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-border/20 bg-app/30">
                      <td colSpan="7" className="p-3 pl-10">
                        <table className="w-full text-xs">
                          <thead><tr className="text-text-muted">
                            <th className="text-left p-1">Código</th>
                            <th className="text-right p-1">Cant.</th>
                            <th className="text-right p-1">Costo unit.</th>
                            <th className="text-right p-1">Costo total</th>
                            <th className="text-left p-1">Batch</th>
                            <th className="text-left p-1">Vence</th>
                            <th className="text-left p-1">Bodega</th>
                          </tr></thead>
                          <tbody>
                            {r.items.map((it, i) => (
                              <tr key={i}>
                                <td className="p-1 font-mono text-agro-green">{it.producto_codigo}</td>
                                <td className="p-1 text-right">{it.cantidad}</td>
                                <td className="p-1 text-right">{fmtL(it.costo_unitario)}</td>
                                <td className="p-1 text-right">{fmtL(it.costo_total)}</td>
                                <td className="p-1">{it.lote || "—"}</td>
                                <td className="p-1">{it.fecha_vencimiento ? fmtDate(it.fecha_vencimiento) : "—"}</td>
                                <td className="p-1">{it.bodega_codigo || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function EstadoBadge({ e, factura }) {
  const map = {
    borrador: "bg-text-muted/10 text-text-secondary border-border",
    aprobada: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    recibida_parcial: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    recibida: "bg-agro-green/10 text-agro-green border-agro-green/30",
    facturada: "bg-agro-green/10 text-agro-green border-agro-green/30",
    emitida: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    pagada: "bg-agro-green/10 text-agro-green border-agro-green/30",
    parcial: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30",
    anulada: "bg-red-500/10 text-red-400 border-red-500/30",
  };
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${map[e] || "border-border"}`}>{factura ? (ESTADO_PAGO[e] || e) : e.replace("_", " ")}</span>;
}

export function PurchaseInvoiceViewer({ inv, onClose, onPdf, onPay, onEdit }) {
  const vm = useModalForm({ onClose });
  const mf = monedaDe(inv);
  const tm = inv.totales_moneda || {};
  const usd = mf === "USD";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Factura de proveedor</div>
            <h3 className="font-heading text-xl font-semibold">{inv.numero_interno}</h3>
            <div className="text-xs text-text-muted">Núm. proveedor: {inv.numero_proveedor} · CAI: {inv.cai_proveedor || "—"}</div>
          </div>
          <div className="flex items-start gap-2">
            <button onClick={onPdf} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary hover:text-agro-green flex items-center gap-1.5" data-testid="pi-viewer-pdf"><FilePdfIcon size={14} /> PDF / Imprimir</button>
            {onEdit && inv.estado !== "anulada" && inv.tipo_documento !== "gasto_compra" && (
              <button onClick={onEdit} className="px-3 py-1.5 text-xs border border-border rounded-md text-text-secondary hover:text-agro-green flex items-center gap-1.5" data-testid="pi-viewer-edit"><PencilSimpleIcon size={14} /> Editar</button>
            )}
            {inv.estado !== "anulada" && inv.saldo_pendiente > 0 && (
              <button onClick={onPay} className="px-3 py-1.5 text-xs bg-agro-green text-black font-medium rounded-md flex items-center gap-1.5" data-testid="pi-viewer-pay"><MoneyIcon size={14} /> Pagar</button>
            )}
            <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted ml-2"><XIcon size={20} /></button>
          </div>
        </div>
        <div className="p-6">
          <div className="bg-app border border-border rounded-md p-3 mb-4">
            <div className="text-[10px] uppercase text-text-muted">Proveedor</div>
            <div className="font-medium">{inv.proveedor_nombre}{inv.subcuenta_nombre && <span className="text-agro-green text-sm"> · subcuenta {inv.subcuenta_codigo} ({inv.subcuenta_pais || inv.subcuenta_nombre})</span>}</div>
            <div className="text-xs text-text-secondary">
              RTN: {inv.proveedor_rtn || "—"} · Fecha: {fmtDate(inv.fecha)} · Términos: {inv.forma_pago}{inv.forma_pago === "credito" && inv.dias_credito ? ` ${inv.dias_credito} días` : ""}
              {inv.fecha_vencimiento ? ` · Vence: ${fmtDate(inv.fecha_vencimiento)}` : ""}
              {inv.purchase_order_numero ? ` · OC: ${inv.purchase_order_numero}` : ""}
              {inv.inventario_actualizado === false ? " · (mercadería ingresada por recepción de OC)" : ""}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {inv.branch_codigo && <>Sucursal {inv.branch_codigo}{inv.auxiliar_codigo ? ` · auxiliar ${inv.auxiliar_codigo}` : ""} · </>}
              Moneda: <b data-testid="pi-viewer-moneda">{usd ? `Dólares (USD) · tasa de la factura L ${inv.tipo_cambio} por US$` : "Lempiras (HNL)"}</b>
              {" · "}Estado: <b>{ESTADO_PAGO[inv.estado] || inv.estado}</b>
            </div>
          </div>
          <table className="w-full mb-4">
            <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
              <th className="p-2">Código</th><th className="p-2">Descripción</th><th className="p-2 text-right">Cant.</th>
              <th className="p-2 text-right">Costo{usd ? " (US$)" : ""}</th><th className="p-2 text-right">ISV</th><th className="p-2 text-right">Total{usd ? " (US$)" : ""}</th>
            </tr></thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} className="border-b border-border/30">
                  <td className="p-2 font-mono text-xs">{it.codigo}</td>
                  <td className="p-2 text-sm">{it.descripcion}</td>
                  <td className="p-2 text-right text-sm">{it.cantidad}</td>
                  <td className="p-2 text-right text-sm">{fmtMon(usd ? it.precio_unitario_moneda : it.precio_unitario, mf)}</td>
                  <td className="p-2 text-right text-xs">{it.isv_pct}%</td>
                  <td className="p-2 text-right text-sm font-medium">{fmtMon(usd ? it.total_moneda : it.total, mf)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end">
            <div className="w-full max-w-sm space-y-1 text-sm">
              <Row l="Gravado 15%" v={fmtMon(usd ? tm.subtotal_gravado_15 : inv.subtotal_gravado_15, mf)} />
              <Row l="Gravado 18%" v={fmtMon(usd ? tm.subtotal_gravado_18 : inv.subtotal_gravado_18, mf)} />
              <Row l="Exento" v={fmtMon(usd ? tm.subtotal_exento : inv.subtotal_exento, mf)} />
              <Row l="ISV 15%" v={fmtMon(usd ? tm.isv_15 : inv.isv_15, mf)} />
              <Row l="ISV 18%" v={fmtMon(usd ? tm.isv_18 : inv.isv_18, mf)} />
              <div className="border-t border-border pt-2 mt-2" data-testid="pi-viewer-totales">
                <Row l={usd ? "TOTAL (original)" : "TOTAL"} v={fmtMon(inv.total_moneda ?? inv.total, mf)} big />
                {usd && <Row l={`Conversión original · ${fmtMon(inv.total_moneda, "USD")} × ${inv.tipo_cambio}`} v={fmtL(inv.total)} />}
                <Row l="Pagado" v={fmtMon((inv.total_moneda ?? inv.total) - saldoMoneda(inv), mf)} />
                <Row l="Saldo pendiente" v={fmtMon(saldoMoneda(inv), mf)} yellow />
                {usd && <Row l="Saldo en CxP (a la tasa de la factura)" v={fmtL(inv.saldo_pendiente)} />}
              </div>
            </div>
          </div>

          {(inv.items || []).some((it) => it.costo_unitario_final) && (
            <div className="mt-4 overflow-x-auto" data-testid="pi-costo-real">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Costo real unitario por producto (en lempiras)</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left text-[10px] uppercase text-text-muted border-b border-border">
                  <th className="p-1.5">Producto</th><th className="p-1.5 text-right">Cant.</th><th className="p-1.5 text-right">Precio unit.</th>
                  <th className="p-1.5 text-right">Flete</th><th className="p-1.5 text-right">Otros gastos</th><th className="p-1.5 text-right">Gasto adic. / unidad</th>
                  <th className="p-1.5 text-right">Costo unit. final</th><th className="p-1.5 text-right">Costo total</th>
                </tr></thead>
                <tbody>
                  {inv.items.filter((it) => it.costo_unitario_final).map((it, i) => {
                    const fl = Number(it.flete_asignado ?? 0), ot = Number(it.otros_gastos_asignados ?? (it.flete_asignado === undefined ? it.gasto_asignado : 0) ?? 0);
                    const cant = Number(it.cantidad) || 0;
                    return (
                      <tr key={i} className="border-b border-border/30" data-testid={`pi-costo-${it.codigo}`}>
                        <td className="p-1.5">{it.codigo} · {it.descripcion}</td><td className="p-1.5 text-right">{cant}</td>
                        <td className="p-1.5 text-right">{fmtL(it.precio_unitario_neto)}</td>
                        <td className="p-1.5 text-right">{fmtL(fl)}</td><td className="p-1.5 text-right">{fmtL(ot)}</td>
                        <td className="p-1.5 text-right">{fmtL(cant ? Number(it.gasto_asignado || 0) / cant : 0)}</td>
                        <td className="p-1.5 text-right font-semibold text-agro-green">{fmtL(it.costo_unitario_final)}</td>
                        <td className="p-1.5 text-right">{fmtL(Number(it.costo_unitario_final) * cant)}</td>
                      </tr>);
                  })}
                </tbody>
              </table>
            </div>
          )}
          {(inv.gastos || []).length > 0 && (
            <div className="mt-4" data-testid="pi-gastos">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Gastos distribuidos al costo ({inv.gastos.length})</div>
              <table className="w-full text-xs"><tbody>
                {inv.gastos.map((g, i) => (
                  <tr key={g.id || i} className="border-b border-border/30">
                    <td className="p-1.5">{g.concepto}</td>
                    <td className="p-1.5 text-right">{fmtMon(g.monto, g.moneda)}{g.moneda !== "HNL" && <span className="text-text-muted"> × {g.tipo_cambio}</span>}</td>
                    <td className="p-1.5 text-right">{fmtL(g.monto_hnl)}</td>
                    <td className="p-1.5">{g.forma_pago === "solo_costo"
                      ? <span className="text-agro-yellow">Solo para el costo · pendiente de contabilizar</span>
                      : <span className="text-text-muted">{g.proveedor_nombre} · {g.documento_numero || g.numero_documento || "contado"}</span>}</td>
                  </tr>))}
              </tbody></table>
              {inv.costo_pendiente_contabilizar > 0 && <div className="text-[11px] text-agro-yellow mt-1">Costo incluido en el inventario aún sin contabilizar: {fmtL(inv.costo_pendiente_contabilizar)}</div>}
            </div>
          )}
          <PagosFactura inv={inv} />
          <HistorialEdiciones inv={inv} />

          {/* Centro de documentos: factura escaneada, comprobante de pago, etc. */}
          <div className="mt-6">
            <DocumentCenter
              entityType="purchase_invoice"
              entityId={inv.id}
              title="Adjuntos (factura escaneada, comprobantes, etc.)"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ReceiveModal({ po, onClose, onSaved }) {
  const [recv, setRecv] = useState(po.items.map((it) => ({
    producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion,
    cantidad_solicitada: it.cantidad, cantidad_pendiente: it.cantidad - (it.cantidad_recibida || 0),
    cantidad_recibida: it.cantidad - (it.cantidad_recibida || 0),
    lote: "", lote_fabricacion: "", fecha_test: "", fecha_fabricacion: "", fecha_vencimiento: "", bodega_id: "", bodega_codigo: "",
    es_semilla: false,
  })));
  const [numProv, setNumProv] = useState("");
  const [caiProv, setCaiProv] = useState("");
  const [crearFactura, setCrearFactura] = useState(true);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: whs }, { data: prods }] = await Promise.all([
          api.get("/warehouses", { params: { activo: true } }),
          api.get("/products"),
        ]);
        const usable = whs.filter((w) => w.tipo !== "transito");
        setWarehouses(usable);
        const principal = usable.find((w) => w.tipo === "principal");
        // Set defaults: bodega principal + flag semilla por producto
        setRecv((prev) => prev.map((r) => {
          const p = prods.find((x) => x.id === r.producto_id);
          return {
            ...r,
            es_semilla: !!(p && p.es_semilla),
            bodega_id: principal ? principal.id : r.bodega_id,
            bodega_codigo: principal ? principal.codigo : r.bodega_codigo,
          };
        }));
      } catch { /* tolerable */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const items = recv.filter((r) => Number(r.cantidad_recibida) > 0).map((r) => {
        const wh = warehouses.find((w) => w.id === r.bodega_id);
        return {
          producto_id: r.producto_id,
          cantidad_recibida: Number(r.cantidad_recibida),
          lote: r.lote || "",
          lote_fabricacion: r.lote_fabricacion || "",
          fecha_test: r.fecha_test || null,
          fecha_fabricacion: r.fecha_fabricacion || null,
          fecha_vencimiento: r.fecha_vencimiento || null,
          bodega_id: r.bodega_id || null,
          bodega_codigo: wh ? wh.codigo : "",
        };
      });
      await api.post(`/purchase-orders/${po.id}/receive`, {
        items, numero_proveedor: numProv, cai_proveedor: caiProv, crear_factura: crearFactura,
      });
      toast.success("Recepción registrada"); onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: { recv, numProv, caiProv, crearFactura }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="receive-modal">
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Recepción de mercancía · Multi-bodega</div>
            <h3 className="font-heading text-lg font-semibold">{po.numero} · {po.proveedor_nombre}</h3>
            <div className="text-xs text-text-muted mt-1">Captura lote, fecha de test (semillas), vencimiento y bodega destino para trazabilidad PEPS.</div>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">N° factura proveedor</label>
              <input value={numProv} onChange={(e) => setNumProv(e.target.value)} placeholder="000-001-01-XXXX" data-testid="receive-num-prov" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div><label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">CAI proveedor</label>
              <input value={caiProv} onChange={(e) => setCaiProv(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>
          <div className="overflow-x-auto border border-border/60 rounded-md">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2 pl-4">Producto</th>
                  <th className="p-2 text-right">Pend.</th>
                  <th className="p-2 text-right">Recibir</th>
                  <th className="p-2">Bodega destino</th>
                  <th className="p-2">Batch</th>
                  <th className="p-2">Lote fabricación</th>
                  <th className="p-2" title="Solo aplica a semillas">F. Test</th>
                  <th className="p-2" title="Solo aplica a productos (no semillas)">F. Fab.</th>
                  <th className="p-2 pr-4" title="Solo aplica a productos (no semillas)">F. Vence</th>
                </tr>
              </thead>
              <tbody>
                {recv.map((r, i) => (
                  <tr key={r.producto_id} className="border-t border-border/40 align-top" data-testid={`receive-row-${r.codigo}`}>
                    <td className="p-2 pl-4 text-xs">
                      <div className="font-mono text-agro-green flex items-center gap-1">
                        {r.codigo}
                        {r.es_semilla && <span className="text-agro-yellow text-[10px] inline-flex items-center gap-0.5" title="Semilla"><PlantIcon size={12} aria-hidden="true" /> Semilla</span>}
                      </div>
                      <div className="text-text-secondary">{r.descripcion}</div>
                    </td>
                    <td className="p-2 text-right text-xs text-agro-yellow">{r.cantidad_pendiente}</td>
                    <td className="p-2">
                      <input type="number" value={r.cantidad_recibida} max={r.cantidad_pendiente} min={0}
                             data-testid={`receive-qty-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].cantidad_recibida = e.target.value; setRecv(n); }}
                             className="w-20 bg-app border border-border rounded px-2 py-1.5 text-xs text-right focus:border-agro-green" />
                    </td>
                    <td className="p-2">
                      <select
                        value={r.bodega_id}
                        data-testid={`receive-bodega-${r.codigo}`}
                        onChange={(e) => { const n = [...recv]; n[i].bodega_id = e.target.value; setRecv(n); }}
                        className="w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green"
                      >
                        <option value="">—</option>
                        {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <input type="text" value={r.lote} placeholder="B-001"
                             data-testid={`receive-lote-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].lote = e.target.value; setRecv(n); }}
                             className="w-24 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green" />
                    </td>
                    <td className="p-2">
                      <input type="text" value={r.lote_fabricacion} placeholder="LF-2026-001"
                             data-testid={`receive-lote-fab-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].lote_fabricacion = e.target.value; setRecv(n); }}
                             className="w-28 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green"
                             title="Lote de fabricación que viene impreso de fábrica/proveedor" />
                    </td>
                    <td className="p-2">
                      <input type="date" value={r.fecha_test} disabled={!r.es_semilla}
                             data-testid={`receive-fecha-test-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_test = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${!r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Fecha de test de germinación (PEPS por esta fecha)" : "Solo aplica a semillas"} />
                    </td>
                    <td className="p-2">
                      <input type="date" value={r.fecha_fabricacion} disabled={r.es_semilla}
                             data-testid={`receive-fecha-fab-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_fabricacion = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Solo aplica a productos (no semillas)" : "Fecha de fabricación del lote"} />
                    </td>
                    <td className="p-2 pr-4">
                      <input type="date" value={r.fecha_vencimiento} disabled={r.es_semilla}
                             data-testid={`receive-vence-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_vencimiento = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Solo aplica a productos (no semillas)" : "Fecha de vencimiento del lote"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <input id="crear-fact" type="checkbox" checked={crearFactura} onChange={(e) => setCrearFactura(e.target.checked)} className="w-4 h-4 accent-agro-green" />
            <label htmlFor="crear-fact" className="text-sm">Crear factura automáticamente al recibir todo <span className="text-[11px] text-text-muted">(desmarca si la factura llegará después: podrás facturar lo recibido desde la OC sin volver a mover inventario)</span></label>
          </div>
          <button disabled={m.saving} type="submit" data-testid="receive-submit" className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3">Confirmar recepción</button>
        </form>
      </div>
    </div>
  );
}

function Row({ l, v, big, yellow }) {
  return (
    <div className="flex justify-between">
      <span className={`${big ? "font-heading text-base" : "text-xs"} text-text-secondary`}>{l}</span>
      <span className={`${big ? "font-heading text-lg font-semibold text-agro-green" : yellow ? "text-agro-yellow font-medium" : "text-sm"}`}>{v}</span>
    </div>
  );
}

function SendPOEmailModal({ po, onClose }) {
  const [emails, setEmails] = useState("");
  const [subject, setSubject] = useState(`Orden de Compra ${po.numero} · Agroport Honduras`);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    (async () => {
      if (po.proveedor_id) {
        try {
          const { data } = await api.get(`/suppliers/${po.proveedor_id}`);
          if (data?.email) setEmails(data.email);
        } catch { /* ignore */ }
      }
    })();
  }, [po.proveedor_id]);

  const submit = async (e) => {
    e.preventDefault();
    const list = emails.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Agrega al menos un correo destinatario"); return; }
    setSending(true);
    try {
      await api.post(`/purchase-orders/${po.id}/send-email`, {
        to: list, subject, message: message || undefined,
      });
      setSent(true);
      toast.success(`OC enviada a ${list.length} destinatario(s)`);
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };

  const m = useModalForm({ value: { emails, subject, message }, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea enviar el correo?" });
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="send-po-email-modal">
        <form onSubmit={m.guardar}>
          <div className="p-5 border-b border-border">
            <h3 className="font-heading text-lg flex items-center gap-2"><EnvelopeIcon size={20} className="text-agro-green" /> Enviar OC al proveedor</h3>
            <p className="text-xs text-text-muted mt-1">Se enviará el PDF de la orden {po.numero} a {po.proveedor_nombre} desde notificaciones@agroporthonduras.com</p>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Destinatarios <span className="text-red-400">*</span></label>
              <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="proveedor@email.com, otro@email.com" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none" data-testid="po-email-to" autoFocus />
              <div className="text-[10px] text-text-muted mt-1">Separa varios correos con coma.</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Asunto</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none" data-testid="po-email-subject" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-muted font-medium mb-1.5 block">Mensaje adicional <span className="text-text-muted/70 normal-case">(opcional)</span></label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Ej: Por favor confirmar entrega para el día acordado…" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none resize-none" data-testid="po-email-message" />
            </div>
          </div>
          <div className="p-4 border-t border-border flex justify-end gap-2 bg-app/40">
            <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:bg-white/5" data-testid="po-email-cancel">Cancelar</button>
            <button type="submit" disabled={(sending || sent) || m.saving} className="px-4 py-2 text-sm bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-50 flex items-center gap-1.5" data-testid="po-email-send">
              {sending ? (<><SpinnerIcon size={14} className="animate-spin" /> Enviando…</>) :
                sent ? (<><CheckIcon size={14} weight="bold" /> Enviado</>) :
                (<><PaperPlaneTiltIcon size={14} weight="bold" /> Enviar OC</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
